<script setup lang="ts">
import type { ButtonProps } from 'primevue/button';
import type { DialogPassThroughOptions } from 'primevue/dialog';
import { computed } from 'vue';

import useQaAutoId from '@modules/core/qa-automation/utils/useQaAutoId';
import { isUndefined } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type {
  VDialogEmits,
  VDialogProps,
  VDialogSlots,
} from './types';

const {
  dismissableMask = false,
  closeOnEscape = true,
  position = 'center',
  size = 'small',
  visible,
  noMinSize,
} = defineProps<VDialogProps>();

const emit = defineEmits<VDialogEmits>();

defineSlots<VDialogSlots>();

const visibleModel = defineModel<boolean | undefined>('visible');

const { getQaAutoIdAttribute } = useQaAutoId();

const closeButtonProps: ButtonProps = { severity: 'secondary', text: true };

const rootClass = computed<string>(() => {
  const classes: string[] = ['w-full'];

  if (size === 'large') {
    classes.push('max-w-[900px] m-3');
  } else {
    classes.push('sm:w-auto max-w-[calc(100vw-40px)]');
  }

  if (position === 'top') {
    classes.push('max-h-[calc(100vh-40px)]');
  }

  if (!noMinSize) {
    classes.push('sm:min-w-[450px]');
  }

  return classes.join(' ');
});

const ptDialog = computed<DialogPassThroughOptions>(() => ({
  root: {
    class: rootClass.value,
    ...getQaAutoIdAttribute(),
  },
  headerActions: {
    class: 'mb-auto',
  },
}));

watchImmediate(() => visible, (newValue) => {
  if (!isUndefined(newValue)) {
    visibleModel.value = newValue;
  }
});
</script>

<template>
  <PDialog
    v-model:visible="visibleModel"
    modal
    block-scroll
    :draggable="false"
    :dismissable-mask="dismissableMask"
    :close-on-escape="closeOnEscape"
    :position="position"
    :pt="ptDialog"
    :close-button-props="closeButtonProps"
    @show="emit('show')"
    @hide="emit('hide')"
    @after-hide="emit('after-hide')"
  >
    <template
      #header
    >
      <slot name="title">
        <div
          class="pr-2 text-xl font-medium word-break"
        >
          {{ title }}
        </div>
      </slot>
    </template>
    <template #closeicon>
      <i class="pi pi-times" />
    </template>
    <template #default>
      <slot />
    </template>
    <template
      v-if="$slots.container"
      #container
    >
      <slot name="container" />
    </template>
    <template
      v-if="$slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
  </PDialog>
</template>
